import { ArrowRight } from "lucide-react";
import { OrbitField } from "./OrbitField";
import { Reveal } from "./Reveal";
import { usePointerField } from "./usePointerField";

const COORDS = ["Design", "Build", "Deploy", "Operate"];

export function Hero() {
  const ref = usePointerField<HTMLElement>();

  return (
    <section
      ref={ref}
      id="top"
      className="relative isolate flex min-h-[100svh] flex-col justify-end overflow-hidden pb-8 pt-32 sm:pb-12 lg:min-h-screen"
    >
      <OrbitField />

      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 noise-veil opacity-[0.05] mix-blend-screen"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(120% 80% at 50% 0%, transparent 35%, color-mix(in oklab, var(--background) 85%, transparent) 100%)",
        }}
      />

      <div className="relative mx-auto w-full max-w-[1400px] px-6 md:px-10">
        <Reveal className="flex items-center gap-4">
          <span className="h-px w-10 bg-primary" />
          <p className="eyebrow">Billionaire Mindset Technologies</p>
        </Reveal>

        <Reveal delay={120}>
          <h1 className="mt-7 max-w-[17ch] font-display text-[clamp(2.6rem,8.4vw,6.75rem)] leading-[0.92] tracking-[-0.04em]">
            Engineering the <span className="text-gradient-brand">systems</span> behind
            what comes next.
          </h1>
        </Reveal>

        <div className="mt-10 grid gap-8 border-t border-hairline pt-8 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
          <Reveal delay={220}>
            <p className="max-w-2xl text-balance text-base leading-relaxed text-muted-foreground sm:text-lg">
              We design, build, deploy and operate modern software, working at the
              intersection of product engineering and cloud infrastructure — where careful
              architecture, disciplined delivery and durable systems decide whether an idea
              actually ships.
            </p>
          </Reveal>

          <Reveal delay={320} className="flex flex-wrap items-center gap-4">
            <a
              href="#expertise"
              className="group relative inline-flex items-center gap-3 overflow-hidden bg-primary px-7 py-4 text-[0.75rem] tracked text-primary-foreground"
            >
              <span
                aria-hidden="true"
                className="absolute inset-0 -translate-x-full bg-white/15 transition-transform duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:translate-x-0"
              />
              <span className="relative">Our expertise</span>
              <ArrowRight className="relative size-4 transition-transform duration-500 group-hover:translate-x-1" />
            </a>
            <a
              href="#approach"
              className="group relative inline-flex items-center gap-3 px-7 py-4 text-[0.75rem] tracked text-foreground ring-1 ring-inset ring-hairline transition-[box-shadow,background-color] duration-500 hover:bg-primary/5 hover:ring-primary/50"
            >
              How we work
            </a>
          </Reveal>
        </div>

        {/* Instrument strip — the lifecycle as coordinates */}
        <Reveal delay={420}>
          <ul className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-3 border-t border-hairline pt-5 sm:mt-10">
            {COORDS.map((c, i) => (
              <li key={c} className="flex items-center gap-3">
                <span className="num text-[0.65rem] text-primary">0{i + 1}</span>
                <span className="text-[0.65rem] tracked text-muted-foreground">{c}</span>
                {i < COORDS.length - 1 && (
                  <span aria-hidden="true" className="ml-5 hidden h-px w-8 bg-hairline sm:block" />
                )}
              </li>
            ))}
          </ul>
        </Reveal>
      </div>
    </section>
  );
}
